import React, { useCallback, useEffect, useState } from "react";
import OpinionFromMemoField from "@/components/forms/OpinionFromMemoField";   // Step 12 add-on G1
import { refText } from "@/lib/s12f2";   // Step 12 add-on G2 (owner F2 Q2): the EQ-review wording in AR
import { setUnsaved } from "@/lib/unsavedChanges";   // Batch 27 · Add-on 3 · C (M41)
import { fmtDateTimeFirm } from "@/lib/dt";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AutoGrowTextarea } from "@/components/ui/auto-grow-textarea";   // Batch 23 · Stages 1–2: a box that shows all its text
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { DialogDescription, DialogFooter } from "@/components/ui/dialog";
import FullScreenDialog from "@/components/FullScreenDialog";
import { Save, Info, Sparkles } from "lucide-react";
import AttachmentPanel from "@/components/AttachmentPanel";
import SignOffBar from "@/components/SignOffBar";
import Legacy316aNotice from "@/components/forms/Legacy316aNotice";   // PR 51 · P51-B2
import { isLegacy316a } from "@/lib/farForms";
import SignoffBarField from "@/components/forms/SignoffBarField";   // Batch 31 · item 27 (M103)
import DateInput from "@/components/DateInput";
import MaterialityFiguresPanel from "@/components/MaterialityFiguresPanel";
import ResponsePlanSummary from "@/components/ResponsePlanSummary";
import { useI18n } from "@/contexts/I18nContext";

// Batch A · L2 root-cause guard. If the module ever resolves to
// undefined at import time (broken syntax, HMR race, circular import),
// log a loud console error ONCE and render an inline placeholder
// instead of silently hiding the block. The default-export function
// signature is verified here at module-load, not inside the render.
// HOTFIX dead links (owner 30-Oct-2026): the record-fed note of 105(a) sections 1 / 2 / 4 links to the Materiality figures
// panel of the same form (105(a) lives on the Planning tab; FormsPage opens ?openForm= and scrolls to ?highlight=).
const MATERIALITY_PANEL_ID = "materiality-figures-panel";
const MATERIALITY_PANEL_HREF = `/planning?openForm=materiality&highlight=${MATERIALITY_PANEL_ID}`;
function scrollToMaterialityPanel(e) {
  const el = document.querySelector(`[data-testid="${MATERIALITY_PANEL_ID}"], [data-testid="mat-panel-fallback"]`);
  if (!el) return;              // not in 105(a): follow the href
  e.preventDefault();
  if (el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const _matPanelOk = typeof MaterialityFiguresPanel === "function" || (MaterialityFiguresPanel && typeof MaterialityFiguresPanel.render === "function");   // Batch 9 · A7: a forwardRef component is an object
if (!_matPanelOk) {
  /* eslint-disable no-console */
  console.error("[FormRenderer] MaterialityFiguresPanel import is not a function.",
    { got: MaterialityFiguresPanel });
  /* eslint-enable no-console */
}
import { fmtPlural } from "@/lib/format";
import Tcwg311aLog from "@/components/Tcwg311aLog";
import { asArray } from "@/lib/asArray";
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import { TableSection, sectionSpansRow } from "@/components/forms/FormTableSection";   // Batch 23 · Stages 1, 6(a)

const YES_NO_NA = ["Yes", "No", "N/A"];

/**
 * Generic renderer for a single audit form. Renders all section types
 * described in /app/backend/forms_library.json.
 */
export default function FormRenderer({ engagementId, formId, highlight, onClose, readOnly: readOnlyProp = false }) {
  const { lang, dict, t } = useI18n();
  const matRef = React.useRef(null);   // Batch 9 · A7: the 105(a) figures panel saves with the form
  const [payload, setPayload] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Batch 27 · Add-on 3 · C (M41): an unsaved form keeps the page from reloading by itself
  useEffect(() => { setUnsaved(`form:${formId || ""}`, dirty); return () => setUnsaved(`form:${formId || ""}`, false); }, [dirty, formId]);
  // H3 — When rendering `engagement_letter` or `client_acceptance_continuance`
  // on a Review engagement, prepend the ISRE 2400 (Revised) review-wording
  // variant so the auditor sees limited-assurance wording. J1 — the
  // full engagement profile (not just the type) is fetched so per-
  // section grey-not-hide state is available at render time.
  const [engProfile, setEngProfile] = useState(null);
  const engType = engProfile?.engagement_type || "audit";
  const reloadProfile = useCallback(() => {
    if (!engagementId) return;
    return api.get(`/engagements/${engagementId}/profile`, { _silent: true })
      .then((r) => setEngProfile(r.data))
      .catch(() => {});
  }, [engagementId]);
  useEffect(() => { reloadProfile(); }, [reloadProfile]);
  const pageEnablements = engProfile?.page_enablements || {};
  const showReviewVariant = engType === "review" &&
    (formId === "engagement_letter" || formId === "client_acceptance_continuance");
  const reviewTitle = formId === "engagement_letter"
    ? t("reviewForms.elReviewTitleEN") : t("reviewForms.cacReviewTitleEN");
  const reviewTitleAr = formId === "engagement_letter"
    ? t("reviewForms.elReviewTitleAR") : t("reviewForms.cacReviewTitleAR");
  const reviewBody = formId === "engagement_letter"
    ? t("reviewForms.elReviewBodyEN") : t("reviewForms.cacReviewBodyEN");
  const reviewBodyAr = formId === "engagement_letter"
    ? t("reviewForms.elReviewBodyAR") : t("reviewForms.cacReviewBodyAR");
  // J1(a) — sub-title override for 106(a) / 100(a) on Review engagements.
  const reviewSubtitle = formId === "engagement_letter"
    ? t(lang === "ar" ? "reviewForms.elSubtitleAR" : "reviewForms.elSubtitleEN")
    : t(lang === "ar" ? "reviewForms.cacSubtitleAR" : "reviewForms.cacSubtitleEN");
  // J1(b) — sections that render greyed on Review engagements.
  const GREY_SECTIONS = new Set([
    "engagement_letter::preconditions",
    "engagement_letter::recurring",
    "client_acceptance_continuance::preconditions",
  ]);
  // C3b · P3 — On Review engagements, the 311(a) form (Communications
  // with TCWG and Management) must NOT show the N/A badge (owner call:
  // 311(a) stays active on reviews) and its audit-wording intro is
  // suppressed in favour of a short "practitioner scope" note rendered
  // by the Tcwg311aLog component. No ISA text is reproduced here.
  const is311aForm = formId === "communications_with_tcwg_and_management";
  const reviewMode311a = engType === "review" && is311aForm;
  // PR 51 · P51-B2: a 316(a) held by a review engagement is read-only here (the opener says so; the profile confirms it)
  const readOnly = readOnlyProp || isLegacy316a(formId, engType);
  const isSectionGreyed = (secId) => {
    if (!showReviewVariant) return false;
    if (!GREY_SECTIONS.has(`${formId}::${secId}`)) return false;
    const key = `form-section:${formId}:${secId}`;
    const enab = pageEnablements[key];
    return !(enab && enab.enabled === true);
  };

  useEffect(() => {
    if (!engagementId || !formId) return;
    api.get(`/forms/${engagementId}/${formId}`)
      .then((r) => { setPayload({ ...r.data, updated_by: r.data?.updated_by_name || r.data?.updated_by }); setDirty(false); })   // Batch 9 · A5 (F-59): the name, never the id
      .catch((e) => toast.error(toErrMsg(e, t("formRenderer.failedLoad"))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagementId, formId]);

  // Batch 14 · M8 (F-107): after an upload / delete the server recomputes "signed letter attached" — re-read that flag
  // only (the dialog's unsaved answers stay as typed)
  const refreshAttachedFlag = React.useCallback(async () => {
    try {
      const r = await api.get(`/forms/${engagementId}/${formId}`, { _silent: true });
      setPayload((p) => (p ? { ...p, attachments_count: r.data?.attachments_count,
                               state: { ...(p.state || {}), signed_letter_attached: !!(r.data?.state || {}).signed_letter_attached } } : p));
    } catch { /* the next load shows it */ }
  }, [engagementId, formId]);

  // Scroll + flash the highlighted section/item/field/row once the form is loaded.
  useEffect(() => {
    if (!payload || !highlight) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="${highlight}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-emerald-500", "ring-offset-2", "transition");
      setTimeout(() => {
        el.classList.remove("ring-2", "ring-emerald-500", "ring-offset-2", "transition");
      }, 2400);
    }, 220);
    return () => clearTimeout(t);
  }, [payload, highlight]);

  // Batch A Round-2 G1/H2 — 209(a) sec 4 auto-select "Standard applied"
  // from period_beginning_date. `lastAutoRef` remembers the last value
  // we wrote automatically; if the current `standard_applied` still
  // matches it (or is empty), we treat the field as *not manually
  // overridden* and free to recompute when the date changes. As soon as
  // the auditor picks a different value, `lastAutoRef` no longer
  // matches, so we stop overwriting and the override banner appears.
  // Hooks MUST run before the early-return path below.
  const _gcState = (payload?.state || {})["4_final_evaluation_and_conclusion"] || {};
  const lastAutoRef = React.useRef(null);
  const gcAutoStandard = React.useMemo(() => {
    if (formId !== "going_concern_events_and_conditions") return null;
    const pb = _gcState.period_beginning_date;
    // Complete YYYY-MM-DD only — partial dates typed on the way don't
    // lock the auto value (H2).
    if (!pb || !/^\d{4}-\d{2}-\d{2}$/.test(pb)) return null;
    const cutoff = new Date("2026-12-15");
    const d = new Date(pb);
    if (isNaN(d.getTime())) return null;
    return d < cutoff ? "ISA 570 (Revised)" : "ISA 570 (Revised 2024)";
  }, [formId, _gcState.period_beginning_date]);

  useEffect(() => {
    if (formId !== "going_concern_events_and_conditions") return;
    if (!gcAutoStandard || !payload) return;
    const s = _gcState;
    const currentStd = s.standard_applied || "";
    const wantAsmt = gcAutoStandard === "ISA 570 (Revised)"
      ? (s.fs_date || "") : (s.fs_approval_date || "");
    const changes = {};
    // Auto-write standard_applied unless the auditor has taken it over.
    if (!currentStd || currentStd === lastAutoRef.current) {
      if (currentStd !== gcAutoStandard) changes.standard_applied = gcAutoStandard;
      lastAutoRef.current = gcAutoStandard;
    } else if (currentStd === gcAutoStandard && lastAutoRef.current == null) {
      // On first load with a saved form whose value already matches the
      // auto value, treat it as a baseline so future date changes can
      // recompute freely.
      lastAutoRef.current = gcAutoStandard;
    }
    if (!s.assessment_period_end && wantAsmt) changes.assessment_period_end = wantAsmt;
    if (Object.keys(changes).length) {
      setPayload((p) => p ? ({
        ...p,
        state: {
          ...(p.state || {}),
          "4_final_evaluation_and_conclusion": {
            ...((p.state || {})["4_final_evaluation_and_conclusion"] || {}),
            ...changes,
          },
        },
      }) : p);
      setDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gcAutoStandard, _gcState.fs_date, _gcState.fs_approval_date, formId]);

  if (!payload) {
    return (
      <FullScreenDialog open={true} onClose={onClose} testid="form-renderer-loading"
                        title={t("formRenderer.loadingForm")}>
          <div className="p-6 text-sm">{t("common.loading")}</div>
      </FullScreenDialog>
    );
  }

  const def = payload.definition;
  // Batch 13 · B2: sections whose answers come from a page record (105(a) → Materiality page) render read-only
  // Batch 13 · B2 (F-95): 105(a) sections fed from the Materiality record (the server names them in state_from_record;
  // this list is the fallback for an older server reply)
  const MATERIALITY_RECORD_SECTIONS = ["1_financial_statement_materiality", "2_performance_materiality", "4_revision_during_the_audit"];
  const recordFed = new Set(payload.state_from_record?.sections || (formId === "materiality" ? MATERIALITY_RECORD_SECTIONS : []));
  const recordSource = payload.state_from_record?.source || "";
  const state = payload.state || {};

  const setSection = (sectionId, next) => {
    setPayload({ ...payload, state: { ...state, [sectionId]: next } });
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      // Batch 9 · A7 (F-61): on 105(a) the Save button also saves the materiality figures (benchmark, amounts, %,
      // clearly trivial, rationale) — the first save needs no reason; a later change keeps the revision log with a reason
      if (formId === "materiality" && matRef.current && typeof matRef.current.saveFromForm === "function") {
        const ok = await matRef.current.saveFromForm();
        if (ok === false) { setBusy(false); return; }
      }
      const r = await api.patch(`/forms/${engagementId}/${formId}`, { state: payload.state });
      // C1-B6 — after Save the footer must show the real status
      // "Saved · DD-MMM-YYYY HH:MM" (EN) / "\u062d\u0641\u0638 \u00b7 …" (AR),
      // not "Not saved yet". Merge server timestamps back into `payload`.
      const nowIso = new Date().toISOString();
      setPayload((p) => ({
        ...(p || {}),
        updated_at: (r?.data && r.data.updated_at) || nowIso,
        updated_by: (r?.data && (r.data.updated_by_name || r.data.updated_by))      // Batch 9 · A5 (F-59): the name first
                        || (p && p.updated_by) || "—",
        carried_forward: null,   // Batch 10.1 · R2 — saved for the current year
      }));
      toast.success(t("formRenderer.savedToast"));
      setDirty(false);
    } catch (e) {
      toast.error(toErrMsg(e, t("formRenderer.failedSave")));
    } finally { setBusy(false); }
  };

  const aiSuggest = async () => {
    if (!await askConfirm(t("formRenderer.aiConfirm"))) return;
    setBusy(true);
    try {
      toast.info(t("formRenderer.aiDrafting"));
      const { data } = await api.post(`/forms/${engagementId}/${formId}/ai-suggest`);
      const nextState = { ...state };
      let count = 0;
      for (const [sid, mapping] of Object.entries(data.suggestions || {})) {
        nextState[sid] = { ...(nextState[sid] || {}) };
        for (const [iid, val] of Object.entries(mapping)) {
          const prev = nextState[sid][iid] || {};
          nextState[sid][iid] = { response: val.response, comment: val.comment, source: "ai",
                                    prior_response: prev.response };
          count++;
        }
      }
      setPayload({ ...payload, state: nextState });
      setDirty(true);
      toast.success(`${t("formRenderer.aiDraftedPrefix")} ${fmtPlural(count, lang, dict.plurals.responses)}${data.prior_year_used ? t("formRenderer.aiDraftedSuffixPY") : ""}${t("formRenderer.aiDraftedTail")}`);
    } catch (e) {
      toast.error(toErrMsg(e, t("formRenderer.aiSuggestFailed")));
    } finally { setBusy(false); }
  };

  const isConditional = (sec) => {
    if (!sec.conditionalOn) return true;
    // Very small evaluator — supports 'field == "value"' referencing any prior section's fields.
    const m = sec.conditionalOn.match(/^([\w_]+)\s*==\s*['"]([^'"]+)['"]$/);
    if (!m) return true;
    const [, key, val] = m;
    for (const s of def.sections) {
      const v = state[s.id]?.[key];
      if (v === val) return true;
    }
    return false;
  };

  // Batch A Round-2 G1 — auto-fill logic moved to top of the component
  // (see hook above). This block is now purely the derived render state.
  return (
    <FullScreenDialog open={true}
                      onClose={async () => { if (dirty && !(await askConfirm(t("formRenderer.discardConfirm")))) return; onClose(); }}
                      testid="form-dialog"
                      title={<span className="flex items-center gap-2 pe-8" data-testid="form-title">
              <span>{(lang === "ar" && def.title_ar) ? def.title_ar : def.title}</span>
              {payload.is_not_applicable && !reviewMode311a && <Badge variant="outline" className="text-[10px]" data-testid="form-na-badge">{t("formRenderer.notApplicable")} — {payload.na_reason ? t("formRenderer.naReason").replace("{reason}", (lang === "ar" && payload.na_reason_ar) || payload.na_reason) : t("formRenderer.naReasonNotRecorded")}</Badge>}   {/* Batch 14 · M11 (F-110) */}
              {payload.carried_forward && (   /* Batch 10.1 · R2 */
                <Badge variant="outline" className="border-sky-300 bg-sky-50 text-[10px] text-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
                       data-testid="form-carried-forward">{t("common.carriedForward")}</Badge>
              )}
      </span>}>
          <div className="w-full border-b bg-white px-6 py-3 dark:bg-slate-900 dark:border-slate-800" data-testid="form-header-block">   {/* Batch 23 · Stage 6(a): full width, no max-w cap */}
            {def.standardsBasis && !showReviewVariant && !reviewMode311a && (
              <DialogDescription className="text-xs" data-testid="form-standards">{def.standardsBasis}</DialogDescription>
            )}
            {showReviewVariant && (
              <DialogDescription className="text-xs" data-testid="form-standards-review"
                                 dir={lang === "ar" ? "rtl" : "ltr"}>
                {reviewSubtitle}
              </DialogDescription>
            )}
            {def.intro && !showReviewVariant && !reviewMode311a && (
              <div className="mt-1 flex items-start gap-2 rounded bg-slate-50 p-2 text-[12px] text-slate-700 dark:bg-slate-800/60 dark:text-slate-200">
                <Info className="mt-0.5 h-3 w-3 flex-none" /><div dir={(lang === "ar" && def.intro_ar) ? "rtl" : undefined}>{(lang === "ar" && def.intro_ar) ? def.intro_ar : def.intro}</div>   {/* Batch 20 · F-140: a form that carries an Arabic intro shows it in Arabic (the nine revised ISA forms) */}
              </div>
            )}
            {readOnly && (
              <Legacy316aNotice engagementId={engagementId} formId={formId} isNA={!!payload.is_not_applicable}
                                onChanged={(na, reason) => setPayload((p) => ({ ...p, is_not_applicable: na, na_reason: reason, na_reason_ar: "" }))} />
            )}
            {reviewMode311a && (
              <div className="mt-1 rounded border border-emerald-200 bg-emerald-50 p-2 text-[12px] text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200"
                   data-testid="form-311a-review-banner"
                   dir={lang === "ar" ? "rtl" : "ltr"}>
                {t("tcwg.reviewBanner")}
              </div>
            )}
            {showReviewVariant && (
              <div data-testid="review-variant-banner"
                    dir={lang === "ar" ? "rtl" : "ltr"}
                    className="mt-1 rounded border border-emerald-300 bg-emerald-50 p-3 text-[12px] text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
                <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wider text-emerald-800">
                  <Info className="h-3 w-3" /> {lang === "ar"
                    ? "المعيار الدولي لارتباطات الفحص \u2066ISRE 2400\u2069 (المُنقّح) — ارتباط فحص"
                    : "ISRE 2400 (Revised) — Review engagement"}
                </div>
                <div className="mb-1 font-semibold"
                     data-testid={lang === "ar" ? "review-variant-title-ar" : "review-variant-title-en"}>
                  {lang === "ar" ? reviewTitleAr : reviewTitle}
                </div>
                <div className="text-[12px]"
                     data-testid={lang === "ar" ? "review-variant-body-ar" : "review-variant-body-en"}>
                  {lang === "ar" ? reviewBodyAr : reviewBody}
                </div>
              </div>
            )}
          </div>

        {/* Batch 11 · A2 / A4 — the sign-off bar replaces the old Mark ready / Approve buttons (FormWorkflowBar) */}
        <div className="w-full px-6 pt-2" data-testid="form-signoff-block">
          <SignOffBar engId={engagementId} itemType="form" itemKey={formId} refreshKey={payload.updated_at} />
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-50/60 dark:bg-slate-950">
          <div className="w-full space-y-4 px-6 py-6" data-testid="form-body-block">   {/* Batch 23 · Stage 6(a): full width, no max-w cap */}
          {/* PR 51 · P51-B2: read-only = every input of the body disabled (no Save, no AI suggest in the footer) */}
          <fieldset disabled={readOnly} className="m-0 min-w-0 space-y-4 border-0 p-0" data-testid="form-body-fieldset">
          <AttachmentPanel engagementId={engagementId}
                            contextType="form" contextId={formId}
                            label={t("formRenderer.supportDocs")}
                            onChange={refreshAttachedFlag} />

          {/* Batch A1 — 105(a) Materiality: the figures block replaces
              the old text-only sections 1/2/4. Section 3 (Yes/No spec
              questions) still renders below as normal. The `typeof`
              guard prevents the whole form from blanking with
              "Element type is invalid" if the module fails to load
              (HMR races, cache staleness). */}
          {/* Batch B · Round B2 · D11 — read-only Response Plan
              summary at the top of 108(a) Detailed Audit Plan and
              202(a) Risk Report — Risk Addressed. Data-only; nothing
              is editable here. */}
          {(formId === "detailed_audit_plan"
              || formId === "risk_report_risk_addressed") && (
            <ResponsePlanSummary engagementId={engagementId} />
          )}
          {formId === "materiality" && (
            _matPanelOk
              ? <MaterialityFiguresPanel engagementId={engagementId} ref={matRef} onDirty={() => setDirty(true)} />
              : <div className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300"
                     data-testid="mat-panel-fallback">
                  Materiality figures block failed to load — see browser console.
                </div>
          )}
          {def.type === "letter" && (
            <div>
              <Label>{t("formRenderer.letterBody")}</Label>
              <Textarea rows={16}
                        value={state.letter_body || ""}
                        onChange={(e) => setSection("letter_body", e.target.value)}
                        placeholder={t("formRenderer.letterPlaceholder")}
                        data-testid="letter-body" />
              <p className="mt-1 text-[11px] text-muted-foreground">
                {def.requiresDateAlignment}
              </p>
              {/* Batch 13 · B3 (F-96): a letter form is answered when the body is present, the signed letter is attached
                  and the date of the letter is entered (DD-MMM-YYYY) */}
              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <Label>{t("formRenderer.letterDate")}</Label>
                  <DateInput value={state.letter_date || ""} onChange={(v) => setSection("letter_date", v)} data-testid="letter-date" />
                </div>
                <div className="self-end text-[12px]" data-testid="letter-attached">
                  {state.signed_letter_attached
                    ? <span className="text-emerald-700">✓ {t("formRenderer.letterAttached")}</span>
                    : <span className="text-amber-700">{t("formRenderer.letterNotAttached")}</span>}
                </div>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground" data-testid="letter-rule">{t("formRenderer.letterRule")}</p>
            </div>
          )}

          {def.type === "document_repository" && (
            <div className="text-sm text-slate-700">
              {/* `def.note` is a string on 312(a) / 308(a) but `{title, text}`
                  on 3 other forms (306(a), 105(a), 205(a)). Those three
                  are type=None today so they never reach this branch, but
                  the coercion below stops React #31 if that changes. */}
              {typeof def.note === "string" ? (
                <p className="mb-2">{def.note}</p>
              ) : def.note && typeof def.note === "object" ? (
                <>
                  {def.note.title && <p className="mb-1 font-medium">{def.note.title}</p>}
                  {def.note.text && <p className="mb-2">{def.note.text}</p>}
                </>
              ) : null}
              <p className="text-[12px] text-muted-foreground">
                {t("formRenderer.docRepoNote").split("{docReview}").map((chunk, idx, arr) => (
                  <React.Fragment key={idx}>
                    {chunk}
                    {idx < arr.length - 1 && <b>{t("formRenderer.documentReviewTab")}</b>}
                  </React.Fragment>
                ))}
              </p>
            </div>
          )}

          {/* Batch 23 · Stage 6(a) (owner 14-Oct-2026 14:40): on a wide window the sections flow in TWO columns (three from
              2200 px) — CSS columns, so the reading order stays top-to-bottom, then the next column, and RTL mirrors by itself.
              Tables and other wide sections span the full row (column-span: all). Order, numbers and data do not change. */}
          <div className="form-section-flow columns-1 gap-4 min-[1400px]:columns-2 min-[2200px]:columns-3" data-testid="form-section-flow">
          {def.sections?.filter((sec) => {
            // Batch A1 — for 105(a), sections 1/2/4 were free-text
            // duplicates of the figures block. Hide them from render;
            // keep section 3 (specific materiality + clearly-trivial
            // Yes/No questions). Old form_data answers are preserved
            // per R7 — they just don't render anymore.
            // Batch 13 · B2 (F-95): sections 1 / 2 / 4 are shown again, READ-ONLY, from the Materiality record (one
            // source; the server fills them on load) — see `recordFed` below
            if (formId === "materiality" && false) return false;
            // Q5 — on Review engagements, ISA 265 section 2 is not
            // applicable (no financial statement audit → no
            // significant deficiency communication under ISA 265). The
            // rows are preserved in form_data (R7) — the section just
            // doesn't render.
            if (formId === "communications_with_tcwg_and_management"
                && engType === "review"
                && sec.id === "2_internal_control_deficiencies_isa_265") return false;
            return isConditional(sec);
          }).map((sec) => {
            if (isSectionGreyed(sec.id)) {
              return (
                <div key={sec.id} className="mb-4 break-inside-avoid">
                <GreyedSectionCard engagementId={engagementId}
                                    formId={formId} sectionId={sec.id}
                                    title={sec.title} onEnabled={reloadProfile} />
                </div>
              );
            }
            return (
            <div key={sec.id} className={`mb-4 break-inside-avoid rounded border bg-white p-3 dark:bg-slate-900 ${sectionSpansRow(sec) ? "[column-span:all]" : ""}`}
                 data-span={sectionSpansRow(sec) ? "row" : "column"} data-testid={`section-${sec.id}`}>
              {sec.id !== "3_communication_log" && (
                /* Q4 — the 311(a) log renders its own translated
                   section title from `t("tcwg.sectionTitle")`; the
                   library title ("3. Communication log") is English
                   and duplicates it on the AR screen. */
                <div className="mb-2 font-medium text-sm">{
                  (() => {
                    // Q5 — on Review engagements, 311(a) section 1
                    // uses the "required communications" title without
                    // the ISA 260 (R) reference. Falls back to the
                    // library title on audit.
                    if (engType === "review"
                        && formId === "communications_with_tcwg_and_management") {
                      const key = `tcwg.reviewOverrides.section1Title`;
                      const alt = t(key);
                      if (alt && alt !== key) return alt;
                    }
                    return sec.title;
                  })()
                }</div>
              )}
              {/* Batch A4 · C7 — 209(a) section 4 header hint for the
                   date-driven standard rule. */}
              {formId === "going_concern_events_and_conditions"
                && sec.id === "4_final_evaluation_and_conclusion" && (
                <p className="mb-2 rounded-md border border-emerald-200 bg-emerald-50/40 p-2 text-[11px] text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-200"
                   data-testid="gc-standard-hint">
                  {t("gc.impact.standardHint")}
                </p>
              )}

              {recordFed.has(sec.id) && (
                <div className="mb-2 flex flex-wrap items-center gap-2 rounded border border-indigo-200 bg-indigo-50 p-2 text-[11px] text-indigo-900 dark:border-indigo-900 dark:bg-indigo-950/20 dark:text-indigo-200"
                     data-testid={`record-fed-${sec.id}`}>
                  <Info className="h-3 w-3" /> {t("formRenderer.fromRecord").replace("{source}", formId === "materiality" ? t("formRenderer.fromRecordSourceMateriality") : recordSource)}
                  {/* HOTFIX dead links: there is no /materiality page — the figures are edited in the Materiality figures panel at the
                       top of 105(a). Already in 105(a): scroll to the panel; the href (105(a) on Planning + highlight) is the fallback. */}
                  <a href={MATERIALITY_PANEL_HREF} className="underline" data-testid={`record-fed-open-${sec.id}`}
                     onClick={scrollToMaterialityPanel}>{t("formRenderer.fromRecordOpen")}</a>
                </div>
              )}
              {sec.type === "fields" && (
                <>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {sec.fields.map((f) => {
                      // Batch A Round-2 F5/G1/G2 — 209(a) sec 4 label +
                      // option localisation. No internal key names on
                      // screen. Conclusion options display as
                      // "N — <spec label>" but store the numeric value
                      // (1..5) so the regex-driven impact block and the
                      // audit trail both stay stable.
                      let field = f;
                      if (formId === "going_concern_events_and_conditions"
                          && sec.id === "4_final_evaluation_and_conclusion") {
                        if (f.id === "reporting_impact_notes") {
                          field = { ...f, label: t("gc.additionalNotes") };
                        } else if (f.id === "standard_applied") {
                          field = { ...f, label: t("gc.standardApplied") };
                        } else if (f.id === "conclusion") {
                          const opts = ["1", "2", "3", "4", "5"];
                          field = {
                            ...f,
                            options: opts,
                            optionLabels: opts.reduce(
                              (m, n) => ({ ...m, [n]: `${n} — ${t(`gc.conclusion.${n}`)}` }), {}),
                          };
                        }
                      }
                      if (formId === "modified_opinion" && f.id === "opinion_type") {
                        if (payload?.read_only_field) {   // Step 12 add-on G1: an unlocked engagement — read-only, from the memo
                          return <OpinionFromMemoField key={f.id} field={f} value={(state[sec.id] || {})[f.id] || ""}
                                                       href={payload.read_only_field.href} />;
                        }
                        field = { ...f, optionLabels: opinionLabels(t) };   // owner F2 Q4: a locked engagement shows its stored value
                      }
                      if (f.from_signoff_bar) {   // Batch 31 · item 27 (M103): shown from the sign-off bar, not typed
                        return <SignoffBarField key={f.id} engId={engagementId} formId={formId} field={field} lang={lang} />;
                      }
                      return (
                        <FieldInput key={f.id} field={field} readOnly={recordFed.has(sec.id)}
                                    value={(state[sec.id] || {})[f.id] || ""}
                                    onChange={(v) => setSection(sec.id, { ...(state[sec.id] || {}), [f.id]: v })} />
                      );
                    })}
                  </div>
                  {/* Batch A Round-2 G1 — override-required banner. Shown
                       when standard_applied disagrees with the
                       auto-computed value from period_beginning_date. */}
                  {formId === "going_concern_events_and_conditions"
                    && sec.id === "4_final_evaluation_and_conclusion" && (() => {
                      const s = state[sec.id] || {};
                      if (!gcAutoStandard || !s.standard_applied) return null;
                      if (s.standard_applied === gcAutoStandard) return null;
                      const needsReason = !((s.standard_override_reason || "").trim());
                      return (
                        <div className={`mt-3 rounded-md border p-2 text-xs ${needsReason
                          ? "border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/20 dark:text-red-200"
                          : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-200"}`}
                             data-testid="gc-standard-override-banner">
                          {t("gc.standardOverrideBanner")}
                        </div>
                      );
                    })()}
                  {/* Batch A4 · C7 — Impact text + date-after-report warning
                       for 209(a) section 4. Read only from local state. */}
                  {formId === "going_concern_events_and_conditions"
                    && sec.id === "4_final_evaluation_and_conclusion" && (() => {
                      const s = state[sec.id] || {};
                      const conc = String(s.conclusion || "");
                      // conclusion strings begin with "N — …"
                      const idx = /^([1-5])/.exec(conc)?.[1];
                      const impactKey = idx ? `gc.impact.opt${idx}` : null;
                      // Batch A Round-2 F5 — heading + auto-filled read-only
                      // impact text (blank on conclusion 1) + 318(a) link.
                      const hasImpact = idx && idx !== "1";
                      const cd = s.conclusion_date, rd = s.report_date;
                      const dateWarn = cd && rd && new Date(cd) > new Date(rd);
                      return (
                        <div className="mt-3 space-y-2" data-testid="gc-reporting-impact-block">
                          {idx && (
                            <div className="rounded-md border border-emerald-300 bg-emerald-50/50 p-2 text-xs text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-200">
                              <div className="mb-1 font-semibold" data-testid="gc-reporting-impact-label">
                                {t("gc.reportingImpactLabel")}
                              </div>
                              <div data-testid="gc-impact-text">
                                {hasImpact ? t(impactKey) : t("gc.reportingImpactEmpty")}
                              </div>
                              {hasImpact && (
                                <a href="/completion?openForm=modified_opinion"
                                   className="mt-1 inline-block text-[11px] underline hover:text-emerald-700"
                                   data-testid="gc-impact-link-318a">
                                  {t("gc.reportingImpactLinkTo318a")}
                                </a>
                              )}
                            </div>
                          )}
                          {dateWarn && (
                            <div className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/20 dark:text-red-200"
                                 data-testid="gc-date-warning">
                              {t("gc.impact.dateWarning")}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                </>
              )}

              {sec.type === "checklist" && (
                <div className="space-y-2">
                  {sec.items.map((it) => {
                    // Carry-over 7 — 106(a) "content" section items get
                    // review-wording overrides on Review engagements.
                    // Saved answers unaffected (item id stays the same).
                    let item = it;
                    if (showReviewVariant && formId === "engagement_letter"
                        && sec.id === "content") {
                      const key = `reviewFormItems.engagement_letter.content.${it.id}`;
                      const alt = t(key);
                      if (alt && alt !== key) item = { ...it, text: alt };
                    }
                    // Q5 — 311(a) review-wording override: item texts
                    // switch to practitioner/review language, and the
                    // ISA reference is blanked (owner: no ISA text
                    // reproduced unless it maps to Batch E spec).
                    if (engType === "review"
                        && formId === "communications_with_tcwg_and_management"
                        && sec.type === "checklist") {
                      const key = `tcwg.reviewOverrides.${it.id}`;
                      const alt = t(key);
                      if (alt && alt !== key) {
                        item = { ...it, text: alt, ref: "" };
                      } else {
                        // Even when we don't have a full text override
                        // yet, blank the ref so no ISA text bleeds in.
                        item = { ...it, ref: "" };
                      }
                    }
                    return (
                      <ChecklistRow key={it.id} item={item} secId={sec.id}
                                     value={(state[sec.id] || {})[it.id] || {}}
                                     onChange={(v) => setSection(sec.id, { ...(state[sec.id] || {}), [it.id]: v })} />
                    );
                  })}
                </div>
              )}

              {sec.type === "table" && sec.id === "3_communication_log" && (
                /* N2 — 311(a) section 3 is rendered by the dedicated
                   Tcwg311aLog component (add/edit, EN + AR labels,
                   linked to tcwg_311a_log — single source of truth).
                   C3b · P3 — hand `isReview` down so the category
                   label swap and the review note surface on Reviews. */
                <Tcwg311aLog engagementId={engagementId}
                             isReview={engType === "review"}
                             showSectionTitle={true} />
              )}
              {sec.type === "table" && sec.id !== "3_communication_log" && formId !== "adjusted_journal_entries_schedule" && (
                <TableSection section={sec}
                              rows={state[sec.id] || Array.from({ length: sec.templateRowCount || 3 }, () => ({}))}
                              onChange={(rows) => setSection(sec.id, rows)} />
              )}
              {sec.type === "table" && formId === "adjusted_journal_entries_schedule" && (
                /* Batch 13 · AE7 — 302(a)'s register is filled from the posted adjusting entries (one source) and read-only;
                   rows typed before Batch 13 are kept and labelled "manual (before adjusting entries)" */
                <div data-testid="aje-302a-register">
                  <div className="mb-2 flex flex-wrap items-center gap-2 rounded border border-indigo-200 bg-indigo-50 p-2 text-[11px] text-indigo-900 dark:border-indigo-900 dark:bg-indigo-950/20 dark:text-indigo-200">
                    <Info className="h-3 w-3" /> {t("formRenderer.ajeRegisterNote")}
                    <a href="/fieldwork/adjusting-entries" className="underline" data-testid="aje-302a-open">{t("formRenderer.ajeOpen")}</a>
                  </div>
                  <TableSection section={{ ...sec, editable: false }} readOnly manualLabel={t("formRenderer.ajeManualRow")}
                                rows={asArray(state[sec.id]).filter((r) => r && Object.keys(r).some((k) => !k.startsWith("_") && String(r[k] || "").trim()))}
                                onChange={() => {}} />
                </div>
              )}
            </div>
            );
          })}
          </div>
          </fieldset>
          </div>
        </div>

        <DialogFooter className="border-t bg-white px-6 py-3 dark:bg-slate-900 dark:border-slate-800">
          <div className="flex w-full items-center justify-end gap-2" data-testid="form-footer-block">
            <div className="me-auto text-[11px] text-muted-foreground" data-testid="form-saved-line">
              {payload.updated_at
                ? t("formRenderer.savedFooter")
                    .replace("{when}", fmtDateTimeFirm(payload.updated_at))   // STEP 3 · B-7c: the firm zone (was the browser's)
                    .replace("{by}", payload.updated_by || "—")
                : t("formRenderer.notSavedYet")}
            </div>
            {!readOnly && def.sections?.some((s) => s.type === "checklist") && (
              <Button variant="outline" onClick={aiSuggest} disabled={busy} data-testid="form-ai-suggest">
                <Sparkles className="me-2 h-3 w-3 text-emerald-600" /> {t("formRenderer.aiSuggestBtn")}
              </Button>
            )}
            <Button variant="outline" onClick={onClose} data-testid="form-close">{t("formRenderer.close")}</Button>
            {!readOnly && (
              <Button onClick={save} disabled={busy || !dirty} className="bg-slate-900 hover:bg-slate-800" data-testid="form-save">
                <Save className="me-2 h-3 w-3" /> {busy ? t("formRenderer.savingBtn") : t("formRenderer.saveBtn")}
              </Button>
            )}
          </div>
        </DialogFooter>
    </FullScreenDialog>
  );
}


// J1(b) — Greyed section card. On a Review engagement the "ISA 210
// preconditions" section of 106(a) and the "Preconditions for an
// audit" section of 100(a) render as a locked notice with an Enable
// button. Any engagement-team auditor may enable the section for a
// documented review matter (additional or alternative procedure).
// Every toggle is written to `audit_trails`.
function GreyedSectionCard({ engagementId, formId, sectionId, title, onEnabled }) {
  const { t, lang } = useI18n();
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [expand, setExpand] = React.useState(false);
  const isAr = lang === "ar";
  const submit = async () => {
    if (!reason.trim()) { toast.error(t("review.pageEnableReasonRequired")); return; }
    setBusy(true);
    try {
      await api.post(
        `/engagements/${engagementId}/review/pages/enable?page_id=${encodeURIComponent(`form-section:${formId}:${sectionId}`)}`,
        { enabled: true, reason },
        { _silent: true });
      toast.success(t(isAr ? "reviewForms.sectionEnabledOkAR" : "reviewForms.sectionEnabledOkEN"));
      setReason("");
      if (onEnabled) await onEnabled();
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded border border-slate-300 bg-slate-50 p-3 opacity-90 dark:bg-slate-800/60"
         data-testid={`section-greyed-${sectionId}`} dir={isAr ? "rtl" : "ltr"}>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-slate-500">🔒</span>
        <span className="font-medium text-sm text-slate-500 line-through">{title}</span>
      </div>
      <div className="mb-2 rounded bg-slate-200/60 px-2 py-1 text-[11px] font-medium text-slate-700"
           data-testid={`section-greyed-${sectionId}-label`}>
        {t(isAr ? "reviewForms.sectionGreyAR" : "reviewForms.sectionGreyEN")}
      </div>
      {!expand ? (
        <Button size="sm" variant="outline" onClick={() => setExpand(true)}
                data-testid={`section-greyed-${sectionId}-open`}>
          {t(isAr ? "reviewForms.sectionEnableBtnAR" : "reviewForms.sectionEnableBtnEN")}
        </Button>
      ) : (
        <div className="space-y-2 rounded border bg-white p-2 dark:bg-slate-900">
          <div className="text-[12px] font-medium">
            {t(isAr ? "reviewForms.sectionEnableTitleAR" : "reviewForms.sectionEnableTitleEN")}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {t(isAr ? "reviewForms.sectionEnableHintAR" : "reviewForms.sectionEnableHintEN")}
          </p>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder={t("review.enableReasonPh")}
                    className="h-16 text-[11px]"
                    data-testid={`section-greyed-${sectionId}-reason`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={busy}
                    data-testid={`section-greyed-${sectionId}-enable`}>
              {t(isAr ? "reviewForms.sectionEnableBtnAR" : "reviewForms.sectionEnableBtnEN")}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setExpand(false); setReason(""); }}
                    data-testid={`section-greyed-${sectionId}-cancel`}>
              {t("formRenderer.close")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Step 11c-3 · find 38: 318(a) "Opinion type concluded" stores the summary memo's codes; the screen shows them in EN / AR

const OPINION_CODES = ["unmodified", "qualified", "adverse", "disclaimer"];
function opinionLabels(t) {
  return OPINION_CODES.reduce((m, c) => ({ ...m, [c]: t(`s11c3Op.opinion.${c}`) }), {});
}

function FieldInput({ field, value, onChange, readOnly = false }) {
  const { t } = useI18n();
  const testid = `field-${field.id}`;
  if (readOnly) {       // Batch 13 · B2: a record-fed answer is shown, never typed
    return (
      <div className={field.type === "textarea" ? "md:col-span-2" : ""}>
        <Label>{field.label}</Label>
        <div className="min-h-9 whitespace-pre-wrap break-words rounded border bg-slate-50 px-2 py-1.5 text-sm dark:bg-slate-900" dir="auto" data-testid={testid}>{String(value ?? "") || "—"}</div>
      </div>
    );
  }
  if (field.type === "textarea") {
    return (
      <div className="md:col-span-2">
        <Label>{field.label}</Label>
        <Textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      </div>
    );
  }
  if (field.type === "radio") {
    return (
      <div>
        <Label>{field.label}</Label>
        <div className="mt-1 flex flex-wrap gap-2">
          {asArray(field.options).map((opt) => (
            <button key={opt} type="button"
                    onClick={() => onChange(opt)}
                    data-testid={`${testid}-${opt.replace(/\s+/g,"-")}`}
                    className={`rounded border px-2 py-1 text-[12px] ${value === opt ? "bg-slate-900 text-white border-slate-900" : "hover:bg-slate-50"}`}>
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "select") {
    // Batch 23 · Stage 2: a select cannot wrap its text — a field whose options are long takes the whole row, and the chosen
    // option is also the box's tooltip
    const label = (o) => field.optionLabels?.[o] || o;
    const longest = Math.max(0, ...asArray(field.options).map((o) => String(label(o)).length));
    return (
      <div className={longest > 48 ? "md:col-span-2" : ""}>
        <Label>{field.label}</Label>
        <select value={value} onChange={(e) => onChange(e.target.value)}
                data-testid={testid} title={value ? String(label(value)) : undefined}
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm">
          <option value="">{t("formRenderer.selectOption")}</option>
          {asArray(field.options).map((opt) => (
            <option key={opt} value={opt}>{field.optionLabels?.[opt] || opt}</option>
          ))}
        </select>
        {field.note && <div className="mt-1 text-[11px] text-muted-foreground" data-testid={`${testid}-note`}>{field.note}</div>}
      </div>
    );
  }
  if (field.type === "signature") {
    // Q2a — simple typed name + auto-timestamp
    const parts = typeof value === "object" && value ? value : { name: value || "", ts: "" };
    return (
      <div>
        <Label>{field.label}</Label>
        <div className="flex gap-2">
          <Input placeholder={t("formRenderer.typeName")} value={parts.name || ""}
                 onChange={(e) => onChange({ name: e.target.value, ts: e.target.value ? (parts.ts || new Date().toISOString()) : "" })}
                 data-testid={`${testid}-name`} />
          <Input readOnly value={parts.ts ? fmtDateTimeFirm(parts.ts) : ""}
                 placeholder={t("formRenderer.signedAtAuto")} className="w-56 bg-slate-50"
                 data-testid={`${testid}-ts`} />
        </div>
      </div>
    );
  }
  // Batch 12 · L3 (F-91): a date field shows and accepts DD-MMM-YYYY (house DateInput); the stored value stays ISO
  if (field.type === "date") {
    return (
      <div>
        <Label>{field.label}</Label>
        <DateInput value={value} onChange={onChange} data-testid={testid} />
      </div>
    );
  }
  // text / default
  // Batch 23 · Stage 2: a text answer is often a sentence — the box grows to show all of it (no line break can be typed)
  return (
    <div>
      <Label>{field.label}</Label>
      <AutoGrowTextarea value={value} dir="auto"
             onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}

// Step 11c-3 · find 36 (Mars live): many forms reuse item ids across sections (303(a) has item_1 in three sections), so the test
// ids repeated ("item-item_1" three times). The answers were always kept per section (state[section][item]); the test ids now
// carry the section too: item-<section>-<item>[-Yes|-No|-N-A|-comment|-ai-badge].
export function ChecklistRow({ item, secId, value, onChange }) {
  const { t } = useI18n();
  const isAi = value?.source === "ai";
  const tid = secId ? `item-${secId}-${item.id}` : `item-${item.id}`;
  return (
    <div className={`rounded border p-2 ${isAi ? "border-emerald-200 bg-emerald-50/40" : ""}`} data-testid={tid}>
      <div className="text-[13px] text-slate-800">{item.text}</div>
      {item.ref && <div className="mt-0.5 text-[10px] text-muted-foreground">{t("formRenderer.refPrefix").replace("{ref}", refText(t, item.ref))}</div>}
      {isAi && (
        <div className="mt-1 text-[10px] font-medium text-emerald-700" data-testid={`${tid}-ai-badge`}>
          {t("formRenderer.aiSuggestedReview")}
        </div>
      )}
      <div className="mt-2 flex items-start gap-4">
        <div className="flex shrink-0 gap-1 pt-0.5">
          {(item.response || YES_NO_NA).map((r) => (
            <button key={r} type="button"
                    onClick={() => onChange({ ...value, response: r, source: undefined })}
                    data-testid={`${tid}-${r.replace(/[/\s]+/g,"-")}`}
                    className={`rounded border px-2 py-0.5 text-[11px] ${value.response === r ? (r === "Yes" ? "bg-emerald-600 text-white border-emerald-600" : r === "No" ? "bg-red-600 text-white border-red-600" : "bg-slate-500 text-white border-slate-500") : "hover:bg-slate-50"}`}>
              {r}
            </button>
          ))}
        </div>
        {item.comment && (
          <AutoGrowTextarea placeholder={t("formRenderer.commentOptional")} value={value.comment || ""} dir="auto"
                 onChange={(e) => onChange({ ...value, comment: e.target.value, source: value.source === "ai" ? undefined : value.source })}
                 className="min-w-0 flex-1"
                 data-testid={`${tid}-comment`} />
        )}
      </div>
    </div>
  );
}
